import { Category } from '@wix/answers-api';
import {
  ConsumerSource,
  RefinementQuestions,
  SkeletonLoading
} from '@wix/cc-user-facing-common-components';
import { Box } from '@wix/design-system';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FunctionComponent, useContext, useEffect } from 'react';

import { useSearchResultsBi } from './bi';
import NoResultsFound from './NoResultsFound';
import { SearchInput } from './SearchInput';
import SearchResultItems from './SearchResultItems';
import { BI } from '../../constants';
import { Context } from '../../context';
import { DATA_HOOKS } from '../../dataHooks';
import { ArticleSearchResult } from '../../types';
import { useStore } from '../../zustand/stores';
import { ContactSupport } from '../ContactSupport';
const GeneratedAnswerV2 = dynamic(() => import('./GeneratedAnswer'), { ssr: false });

import css from './index.module.scss';


type SearchResultsProps = {
  isContactSupport: boolean;
  isLoading: boolean;
  searchResults: ArticleSearchResult;
  categoriesPaths: Record<string, Category[]>;
  locale: string;
  generatedAnswer: string | null;
  isProhibited: boolean | null;
  isStreaming?: boolean;
  usedArticlesInfo: { [id: string]: string };
  mediaRefsMap: Record<string, string>;
  retryGenerateAnswer: () => Promise<void>;
  onSearch: (newTerm: string, inputType?: string) => void;
  searchTerm?: string;
  refinementQuestions: string[];
  showNoResults?: boolean;
};

export const SearchResults: FunctionComponent<SearchResultsProps> = ({
  isContactSupport,
  isLoading,
  searchResults,
  categoriesPaths,
  generatedAnswer,
  isProhibited,
  isStreaming,
  usedArticlesInfo,
  mediaRefsMap,
  retryGenerateAnswer,
  onSearch,
  searchTerm,
  refinementQuestions,
  showNoResults,
}) => {
  const { searchMethod } = searchResults;
  const {
    query: { term },
    locale,
  } = useRouter();
  const decodedTerm = term ? decodeURIComponent(term as string) : '';
  const { isMobile } = useContext(Context);
  const searchSessionId = useStore((state) => state.biSlice.searchSessionId);
  const { onSuggestedQuestionClick } = useSearchResultsBi(
    term as string,
    locale as string,
    searchSessionId
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (showNoResults && !isContactSupport) {
    return (
      <NoResultsFound
        initialSearchTerm={searchTerm}
        onSearch={onSearch}
      />
    );
  }

  return (
    <Box className={css.searchResultsWrapper} dataHook={DATA_HOOKS.SEARCH_RESULTS}>
      <Box className={css.searchResultsWrapperInner} align="center">
        <Box className={css.searchResultsContent} direction="vertical">
          <SearchInput
            initialSearchTerm={searchTerm}
            retryGenerateAnswer={retryGenerateAnswer}
            onSearch={onSearch}
          />
          {refinementQuestions.length > 0 && !isProhibited &&
            <Box className={css.refinementQuestionsWrapper}>
              <RefinementQuestions
                questions={refinementQuestions}
                onClick={(refinementTerm) => {
                  onSuggestedQuestionClick(refinementTerm);
                  onSearch(refinementTerm, BI.INPUT_TYPES.SUGGESTED_QUESTION);
                }}
                consumerSource={ConsumerSource.HELP_CENTER}
              />
            </Box>
          }
          {isContactSupport ? (
            <ContactSupport searchMethod={searchMethod as string} />
          ) :
            <GeneratedAnswerV2
              searchAnswer={generatedAnswer}
              isProhibited={isProhibited}
              isStreaming={isStreaming || isLoading}
              usedArticlesInfo={usedArticlesInfo}
              mediaRefsMap={mediaRefsMap}
              searchQuestion={decodedTerm}
              retryGenerateAnswer={retryGenerateAnswer}
            />
          }
          {
            searchResults.items.length === 0 || searchResults.isLoading ? (
              <SkeletonLoading isMobile={isMobile} locale={locale as string} />
            ) : (
              <SearchResultItems
                searchResults={searchResults}
                categoriesPaths={categoriesPaths}
              />
            )
          }
        </Box>
      </Box>
    </Box>);
};
